import { Link } from "react-router-dom";
import { Heart, Flame, Stamp } from "lucide-react";
import { toast } from "sonner";
import { money, orderHandoff } from "./api";
import { addPick } from "./picks";
import { useT } from "./i18n";

import { Editable } from "./content";

export const SectionTitle = ({ kicker, title, testId, editKey }) => (
  <div className="mb-4" data-testid={testId}>
    {editKey ? (
      <>
        {kicker !== undefined && (
          <Editable k={`${editKey}.kicker`} label="Section kicker" fallback={kicker} as="p"
            className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-1" />
        )}
        <Editable k={`${editKey}.title`} label="Section title" fallback={title} as="h2"
          className="font-display text-2xl font-semibold tracking-tight text-white" />
      </>
    ) : (
      <>
        {kicker && <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-1">{kicker}</p>}
        <h2 className="font-display text-2xl font-semibold tracking-tight text-white">{title}</h2>
      </>
    )}
  </div>
);

export const TasteDots = ({ label, value }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs text-white/60 tracking-wide">{label}</span>
    <span className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`w-2 h-2 rounded-full ${i <= value ? "bg-[#D4AF37]" : "bg-white/15"}`} />
      ))}
    </span>
  </div>
);

export const VegDot = ({ veg }) => (
  <span
    className={`inline-flex items-center justify-center w-4 h-4 border ${veg ? "border-green-500" : "border-[#8B3A3A]"}`}
    title={veg ? "Vegetarian" : "Non-vegetarian"}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${veg ? "bg-green-500" : "bg-[#8B3A3A]"}`} />
  </span>
);

export const DishCard = ({ dish, compact = false, testId }) => (
  <Link
    to={`/experience/dish/${dish.slug}`}
    data-testid={testId || `dish-card-${dish.slug}`}
    className={`block shrink-0 snap-start rounded-2xl overflow-hidden bg-[#121212] border border-white/10 hover:border-[#D4AF37]/40 transition-colors ${compact ? "w-[160px]" : "w-full"}`}
  >
    <div className={`relative ${compact ? "h-[110px]" : "h-[150px]"}`}>
      <img src={dish.image} alt={dish.name} loading="lazy" className="w-full h-full object-cover" />
      {dish.has_story && (
        <span data-testid={`story-badge-${dish.slug}`} title="This dish has a story"
          className="absolute top-2 left-2 flex items-center gap-1 bg-black/65 border border-[#D4AF37]/60 text-[#D4AF37] rounded-full px-2 py-0.5 text-[8px] font-black tracking-[0.18em]">
          <Stamp size={9} /> STORY
        </span>
      )}
      {dish.spice >= 4 && (
        <span className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
          <Flame size={12} className="text-[#E25822]" />
        </span>
      )}
      {dish.available === false && (
        <span data-testid={`unavailable-badge-${dish.slug}`}
          className="absolute inset-x-0 bottom-0 bg-black/80 text-center text-[#E25822] text-[8px] font-black tracking-[0.2em] py-1">
          UNAVAILABLE TODAY
        </span>
      )}
    </div>
    <div className="p-3">
      <div className="flex items-start gap-1.5">
        <VegDot veg={dish.vegetarian} />
        <p className="text-sm font-semibold leading-tight text-white flex-1 line-clamp-2">{dish.name}</p>
      </div>
      <p className="text-[#D4AF37] text-sm font-bold mt-1">{money(dish.price)}</p>
    </div>
  </Link>
);

export const OrderButton = ({ dish, label, source = "dish", className = "", testId }) => {
  const t = useT();
  if (dish && (dish.available === false || dish.coming_soon))
    return (
      <button disabled data-testid={testId || `order-btn-${dish.slug}`} aria-disabled="true"
        className={`bg-white/10 text-white/40 font-bold rounded-full px-6 py-3 text-sm tracking-widest cursor-not-allowed ${className}`}>
        {dish.coming_soon ? "COMING SOON" : "UNAVAILABLE TODAY"}
      </button>
    );
  return (
  <button
    data-testid={testId || `order-btn-${dish ? dish.slug : "generic"}`}
    onClick={() => (dish ? addPick(dish) : orderHandoff(undefined, source))}
    className={`bg-[#D4AF37] hover:bg-[#F1C40F] text-black font-bold rounded-full px-6 py-3 text-sm tracking-widest transition-colors ${className}`}
  >
    {label || (dish ? t("dish.addToList") : t("dish.myList"))}
  </button>
  );
};

export const rewardToast = (res) => {
  // points display hidden from customers (June 2026) — points still earn/store silently
  (res?.new_achievements || []).forEach((a) =>
    toast(`ACHIEVEMENT UNLOCKED — ${a.name}`, {
      style: { background: "#1A1A1A", color: "#fff", border: "1px solid rgba(212,175,55,0.4)" },
    })
  );
};
